import React, { useEffect, useState } from "react";
import { Box, Button, Chip, Stack } from "@mui/joy";
import {
  TextB as FormatBold,
  TextItalic as FormatItalic,
  ListBullets as FormatListBulleted,
  ListNumbers as FormatListNumbered,
  TextHOne as TitleIcon,
  LinkSimple as LinkIcon,
  BracketsCurly as DataObject,
} from "@phosphor-icons/react";
import { EditorContent, useEditor } from "@tiptap/react";
import { Document } from "@tiptap/extension-document";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Text } from "@tiptap/extension-text";
import { Bold } from "@tiptap/extension-bold";
import { Italic } from "@tiptap/extension-italic";
import { Heading } from "@tiptap/extension-heading";
import { BulletList } from "@tiptap/extension-bullet-list";
import { OrderedList } from "@tiptap/extension-ordered-list";
import { ListItem } from "@tiptap/extension-list-item";
import { Link } from "@tiptap/extension-link";
import { History } from "@tiptap/extension-history";
import { Image } from "@tiptap/extension-image";

const MERGE_TAGS = [
  { label: "First name", value: "{{first_name}}" },
  { label: "Last name", value: "{{last_name}}" },
  { label: "Full name", value: "{{full_name}}" },
  { label: "Company name", value: "{{company_name}}" },
  { label: "Email", value: "{{email}}" },
];

interface Props {
  value: string;
  onChange: (html: string) => void;
}

export const EmailTemplateEditor: React.FC<Props> = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      Bold,
      Italic,
      Heading.configure({ levels: [1, 2] }),
      BulletList,
      OrderedList,
      ListItem,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
      }),
      History,
      Image,
    ],
    content: value || "<p></p>",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => {
    if (editor && !editor.isDestroyed && value !== editor.getHTML()) {
      editor.commands.setContent(value || "<p></p>", { emitUpdate: false });
    }
  }, [editor, value]);

  const [showTags, setShowTags] = useState(false);

  if (!editor) return null;

  const btn = (active: boolean): "solid" | "plain" =>
    active ? "solid" : "plain";

  const insertTag = (value: string) => {
    editor.chain().focus().insertContent(value).run();
    setShowTags(false);
  };

  return (
    <Box
      sx={{ border: "1px solid", borderColor: "divider", borderRadius: "sm" }}
    >
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          p: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexWrap: "wrap",
        }}
      >
        <Button
          size="sm"
          variant={btn(editor.isActive("bold"))}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <FormatBold size={18} />
        </Button>
        <Button
          size="sm"
          variant={btn(editor.isActive("italic"))}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <FormatItalic size={18} />
        </Button>
        <Button
          size="sm"
          variant={btn(editor.isActive("heading", { level: 2 }))}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
        >
          <TitleIcon size={18} />
        </Button>
        <Button
          size="sm"
          variant={btn(editor.isActive("bulletList"))}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <FormatListBulleted size={18} />
        </Button>
        <Button
          size="sm"
          variant={btn(editor.isActive("orderedList"))}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <FormatListNumbered size={18} />
        </Button>
        <Button
          size="sm"
          variant="plain"
          onClick={() => {
            const url = window.prompt("Link URL");
            if (url) editor.chain().focus().setLink({ href: url }).run();
          }}
        >
          <LinkIcon size={18} />
        </Button>
        <Button
          size="sm"
          variant="plain"
          onClick={() => {
            const src = window.prompt("Image URL (https://)");
            if (src && /^https:\/\//i.test(src))
              editor
                .chain()
                .focus()
                .setImage({ src, alt: window.prompt("Image alt text") || "" })
                .run();
          }}
        >
          Image
        </Button>
        <Button
          size="sm"
          variant={showTags ? "solid" : "plain"}
          startDecorator={<DataObject size={18} />}
          onClick={() => setShowTags((v) => !v)}
        >
          Merge tag
        </Button>
      </Stack>
      {showTags && (
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            p: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexWrap: "wrap",
            gap: 0.5,
          }}
        >
          {MERGE_TAGS.map((t) => (
            <Chip
              key={t.value}
              size="sm"
              variant="soft"
              color="primary"
              onClick={() => insertTag(t.value)}
            >
              {t.label}
            </Chip>
          ))}
        </Stack>
      )}
      <Box
        sx={{
          p: 2,
          minHeight: 280,
          "& .ProseMirror": { outline: "none", minHeight: 260 },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};
